/**
 * @file BillingPage.tsx
 * @brief Super Admin → Billing & Pricing dashboard.
 *
 * @details
 * Hash tabs (30-07-2026): Overview + one section at a time so the page
 * stays short. Deep links (`#customer-rates`, mega-menu children) still work.
 *
 * @author DES
 * @date 26-05-2026
 * @updated 30-07-2026 — tabbed sections (not scroll-spy timeline)
 */

import { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { Skeleton } from "@/components/ui/Skeleton";
import {
  GridBand,
  GridBlock,
  GridCell,
  GridCells,
} from "@/features/analytics/components/GridBlock";
import { gridPageBleedClass } from "@/features/analytics/components/gridPageBleed";
import { usePricingConfig, useSystemSettings } from "./api";
import {
  BILLING_SECTION_NAV,
  BILLING_SECTIONS,
  BILLING_ZONE_LABELS,
  type BillingNavZone,
  billingSectionIcon,
  billingTabHref,
  resolveBillingTab,
} from "./billingNav";
import { BillingSectionTabs } from "./components/BillingSectionTabs";
import { CostAlertCard } from "./components/CostAlertCard";
import { CustomerRatesSection } from "./components/CustomerRatesSection";
import { SubscriptionPlansCard } from "./components/SubscriptionPlansCard";
import { RecomputeCostsCard } from "./components/RecomputeCostsCard";
import { ServiceCostsCard } from "./components/ServiceCostsCard";

const ZONE_ORDER: BillingNavZone[] = ["pricing", "plans", "internals"];

const ZONE_BLURBS: Record<BillingNavZone, string> = {
  pricing: "List rates customers pay (per product and currency).",
  plans: "Seat plan seeds and country allowlist. Enterprise pools on Edit Company.",
  internals: "Cost alerts, vendor COGS, and historical recompute.",
};

export function BillingPage() {
  const location = useLocation();
  const settingsQuery = useSystemSettings();
  const pricingQuery = usePricingConfig();
  const activeTab = useMemo(
    () => resolveBillingTab(location.hash),
    [location.hash],
  );

  return (
    <div className={gridPageBleedClass("pb-6")}>
      <GridBlock>
        <BillingSectionTabs activeTab={activeTab} />

        <GridBand id="billing-top" className="py-3">
          <h2 className="text-xl font-semibold tracking-tight">
            Billing & Pricing
          </h2>
          <p className="mt-1 max-w-xl text-sm text-muted-foreground">
            {activeTab === "overview"
              ? "Pick a tab for list rates, seat plans, or internal cost tools. Company overrides live on Edit Company."
              : "Company-specific overrides live on Edit Company."}
          </p>
        </GridBand>

        {activeTab === "overview" ? (
          <OverviewPanel />
        ) : activeTab === BILLING_SECTIONS.customerRates ? (
          settingsQuery.isLoading ? (
            <SectionLoader />
          ) : settingsQuery.isError ? (
            <SectionError message="Couldn't load pricing." />
          ) : (
            <CustomerRatesSection data={settingsQuery.data} />
          )
        ) : activeTab === BILLING_SECTIONS.subscriptionPlans ? (
          settingsQuery.isLoading ? (
            <SectionLoader />
          ) : settingsQuery.isError ? (
            <SectionError message="Couldn't load subscription settings." />
          ) : (
            <SubscriptionPlansCard />
          )
        ) : activeTab === BILLING_SECTIONS.costAlerts ? (
          settingsQuery.isLoading ? (
            <SectionLoader />
          ) : settingsQuery.isError ? (
            <SectionError message="Couldn't load cost alert config." />
          ) : (
            <CostAlertCard data={settingsQuery.data} />
          )
        ) : activeTab === BILLING_SECTIONS.serviceCosts ? (
          <ServiceCostsCard
            data={pricingQuery.data}
            isLoading={pricingQuery.isLoading}
          />
        ) : activeTab === BILLING_SECTIONS.recompute ? (
          <RecomputeCostsCard />
        ) : null}
      </GridBlock>
    </div>
  );
}

function OverviewPanel() {
  return (
    <GridCells className="grid-cols-1 md:grid-cols-3">
      {ZONE_ORDER.map((zone) => {
        const items = BILLING_SECTION_NAV.filter((s) => s.zone === zone);
        return (
          <GridCell key={zone} className="flex flex-col gap-3">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                {BILLING_ZONE_LABELS[zone]}
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                {ZONE_BLURBS[zone]}
              </p>
            </div>
            <ul className="mt-auto space-y-1">
              {items.map((section) => {
                const Icon = billingSectionIcon(section.id);
                return (
                  <li key={section.id}>
                    <Link
                      to={billingTabHref(section.id)}
                      className="inline-flex w-full items-center gap-2 px-0 py-1.5 text-sm font-medium text-foreground transition-colors hover:text-primary"
                    >
                      <Icon className="h-3.5 w-3.5 shrink-0 text-primary" />
                      {section.label}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </GridCell>
        );
      })}
    </GridCells>
  );
}

function SectionLoader() {
  return (
    <GridCell>
      <div className="space-y-3">
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-32 w-full" />
      </div>
    </GridCell>
  );
}

function SectionError({ message }: { message: string }) {
  return (
    <GridCell>
      <p className="text-sm text-destructive">{message}</p>
    </GridCell>
  );
}
